import { useEffect, useMemo, useState } from 'react';
import { getLookupCollection } from '../../services/firebase';
import type {
  BrandAcquisition,
  AcquisitionType,
  AcquisitionDirectness,
} from '../../types';

/** A selectable brand for the "acquired brand" typeahead. */
export interface AcquisitionBrandOption {
  id: string;
  /** Display label (displayName || name). */
  name: string;
  /** Secondary line shown muted (e.g. the doc id / slug). */
  subtitle?: string;
}

interface AcquisitionsEditorProps {
  value: BrandAcquisition[];
  onChange: (next: BrandAcquisition[]) => void;
  /**
   * All candidate acquired brands — ALL brands (not just defunct), minus the
   * brand being edited. Modern brands like Gerald Genta can be acquired and
   * still trade, so there is no defunct restriction.
   */
  brandOptions: AcquisitionBrandOption[];
  disabled?: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();

/**
 * Property-bag flag on an acquisition TYPE (in `lookup_acquisition_types`)
 * marking it as a brand rename captured via a free-text former name rather than
 * a link to a separate brand entity. When the selected type carries it, the
 * card swaps the brand picker + directness dropdown for a "Former name" input.
 */
const USES_FORMER_NAME = 'uses-former-name';

/**
 * Card-stack editor for a brand's {@link BrandAcquisition} array. Each card is
 * one acquisition (acquired brand + type + directness + year + notes + source +
 * confidence). Replaces the defunct-only {@link import('./HeritagePicker')}.
 *
 * Acquisition-type and directness option lists are sourced live from the
 * `lookup_acquisition_types` / `lookup_acquisition_directness` Firestore
 * collections (same loader the rest of the lookups use), so admins can extend
 * them in the Lookup Manager without a code change.
 */
export function AcquisitionsEditor({
  value,
  onChange,
  brandOptions,
  disabled = false,
}: AcquisitionsEditorProps) {
  const [typeOptions, setTypeOptions] = useState<
    { value: string; displayName: string; properties?: string[] }[]
  >([]);
  const [directnessOptions, setDirectnessOptions] = useState<
    { value: string; displayName: string }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [types, directness] = await Promise.all([
          getLookupCollection('lookup_acquisition_types'),
          getLookupCollection('lookup_acquisition_directness'),
        ]);
        if (cancelled) return;
        setTypeOptions(
          types
            .filter((v) => v.isActive)
            .map((v) => ({
              value: v.value,
              displayName: v.displayName || v.value,
              properties: v.properties,
            })),
        );
        setDirectnessOptions(
          directness
            .filter((v) => v.isActive)
            .map((v) => ({ value: v.value, displayName: v.displayName || v.value })),
        );
      } catch (error) {
        console.error('Failed to load acquisition lookups:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const brandById = useMemo(() => {
    const m = new Map<string, AcquisitionBrandOption>();
    for (const b of brandOptions) m.set(b.id, b);
    return m;
  }, [brandOptions]);

  const update = (index: number, patch: Partial<BrandAcquisition>) => {
    onChange(value.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([
      ...value,
      {
        acquiredBrandId: '',
        acquisitionType: 'full_brand_company_acquisition',
        directness: 'direct_watch_brand_to_watch_brand',
      },
    ]);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Acquisitions
      </label>
      <p className="text-xs text-gray-500 mb-2">
        Brands this one has acquired or whose lineage it carries (e.g. Bulgari →
        Gerald Genta, Montblanc → Minerva). The acquired brand can still be
        trading — there is no defunct restriction.
      </p>

      {value.length === 0 && (
        <p className="text-xs text-gray-400 italic mb-2">No acquisitions.</p>
      )}

      <div className="flex flex-col gap-3">
        {value.map((acq, index) => (
          <AcquisitionCard
            key={index}
            acquisition={acq}
            brandOptions={brandOptions}
            brandById={brandById}
            typeOptions={typeOptions}
            directnessOptions={directnessOptions}
            loading={loading}
            disabled={disabled}
            onChange={(patch) => update(index, patch)}
            onRemove={() => remove(index)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="mt-3 text-sm px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
      >
        + Add acquisition
      </button>
    </div>
  );
}

interface AcquisitionCardProps {
  acquisition: BrandAcquisition;
  brandOptions: AcquisitionBrandOption[];
  brandById: Map<string, AcquisitionBrandOption>;
  typeOptions: { value: string; displayName: string; properties?: string[] }[];
  directnessOptions: { value: string; displayName: string }[];
  loading: boolean;
  disabled: boolean;
  onChange: (patch: Partial<BrandAcquisition>) => void;
  onRemove: () => void;
}

function AcquisitionCard({
  acquisition,
  brandOptions,
  brandById,
  typeOptions,
  directnessOptions,
  loading,
  disabled,
  onChange,
  onRemove,
}: AcquisitionCardProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  // A name-change type (carrying the `uses-former-name` property) captures a
  // former name string instead of a link to a separate brand + directness.
  const usesFormerName = (
    typeOptions.find((o) => o.value === acquisition.acquisitionType)
      ?.properties ?? []
  ).includes(USES_FORMER_NAME);

  const selected = acquisition.acquiredBrandId
    ? brandById.get(acquisition.acquiredBrandId)
    : undefined;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...brandOptions].sort((a, b) => a.name.localeCompare(b.name));
    if (!q) return sorted.slice(0, 50);
    return sorted
      .filter((o) =>
        [o.name, o.id, o.subtitle ?? ''].join(' ').toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [brandOptions, search]);

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          {usesFormerName ? (
            <>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Former name
              </label>
              <input
                type="text"
                value={acquisition.formerName ?? ''}
                disabled={disabled}
                placeholder="e.g., the brand's previous name"
                onChange={(e) =>
                  onChange({ formerName: e.target.value || undefined })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                The previous name being recorded — no separate brand entity is
                linked for a name change.
              </p>
            </>
          ) : (
            <>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Acquired Brand
          </label>
          {selected && !open ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-sm rounded-full pl-2.5 pr-1 py-0.5 border bg-brand-50 border-brand-200 text-brand-800">
                <span>{selected.name}</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onChange({ acquiredBrandId: '' });
                    setOpen(true);
                  }}
                  aria-label="Change acquired brand"
                  className="rounded-full hover:bg-black/5 w-4 h-4 inline-flex items-center justify-center"
                >
                  ×
                </button>
              </span>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={search}
                disabled={disabled}
                onFocus={() => setOpen(true)}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOpen(true);
                }}
                placeholder={
                  acquisition.acquiredBrandId && !selected
                    ? `Unknown brand: ${acquisition.acquiredBrandId}`
                    : 'Search brands…'
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              {open && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">
                      No brands match.
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {filtered.map((o) => (
                        <li key={o.id}>
                          <button
                            type="button"
                            onClick={() => {
                              onChange({ acquiredBrandId: o.id });
                              setSearch('');
                              setOpen(false);
                            }}
                            className="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50"
                          >
                            <span className="text-sm text-gray-800 flex-1">
                              {o.name}
                            </span>
                            {o.subtitle && (
                              <code className="text-xs text-gray-400">
                                {o.subtitle}
                              </code>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove acquisition"
          title="Remove acquisition"
          className="mt-5 text-gray-400 hover:text-red-600 disabled:opacity-50"
        >
          {/* trash icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Acquisition Type
          </label>
          <select
            value={acquisition.acquisitionType}
            disabled={disabled || loading}
            onChange={(e) => {
              const nextType = e.target.value as AcquisitionType;
              const nextUsesFormerName = (
                typeOptions.find((o) => o.value === nextType)?.properties ?? []
              ).includes(USES_FORMER_NAME);
              if (nextUsesFormerName) {
                // Name change: drop the brand link + directness; keep formerName.
                onChange({
                  acquisitionType: nextType,
                  acquiredBrandId: undefined,
                  directness: undefined,
                });
              } else {
                // Regular type: drop formerName; ensure a directness default.
                onChange({
                  acquisitionType: nextType,
                  formerName: undefined,
                  directness:
                    acquisition.directness ?? 'direct_watch_brand_to_watch_brand',
                });
              }
            }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100"
          >
            {/* Keep the stored value selectable even if it's not (yet) a lookup option. */}
            {!typeOptions.some((o) => o.value === acquisition.acquisitionType) && (
              <option value={acquisition.acquisitionType}>
                {acquisition.acquisitionType}
              </option>
            )}
            {typeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.displayName}
              </option>
            ))}
          </select>
        </div>

        {!usesFormerName && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Directness
            </label>
            <select
              value={acquisition.directness ?? ''}
              disabled={disabled || loading}
              onChange={(e) =>
                onChange({ directness: e.target.value as AcquisitionDirectness })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100"
            >
              {!directnessOptions.some(
                (o) => o.value === acquisition.directness,
              ) && (
                <option value={acquisition.directness ?? ''}>
                  {acquisition.directness ?? '—'}
                </option>
              )}
              {directnessOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.displayName}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Year
          </label>
          <input
            type="number"
            value={acquisition.year ?? ''}
            disabled={disabled}
            min={1700}
            max={CURRENT_YEAR + 5}
            placeholder="e.g., 2000"
            onChange={(e) =>
              onChange({
                year: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Confidence
          </label>
          <select
            value={acquisition.confidence ?? ''}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                confidence: e.target.value
                  ? (e.target.value as 'high' | 'medium' | 'low')
                  : undefined,
              })
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="">—</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Notes
          </label>
          <textarea
            value={acquisition.notes ?? ''}
            disabled={disabled}
            rows={2}
            placeholder="Context, caveats, source detail…"
            onChange={(e) =>
              onChange({ notes: e.target.value || undefined })
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Source URL
          </label>
          <input
            type="url"
            value={acquisition.sourceUrl ?? ''}
            disabled={disabled}
            placeholder="https://…"
            onChange={(e) =>
              onChange({ sourceUrl: e.target.value || undefined })
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </div>
    </div>
  );
}
